import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HelpModal from '../components/HelpModal';
import { helpGuides } from '../utils/helpGuides';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface Funnel {
  id: number;
  name: string;
  description: string;
  category: string;
  steps: Array<{
    name: string;
    description: string;
    conversionRate?: string;
  }>;
  best_for: string[];
  expected_roi: string;
  is_template: boolean;
  created_by: string;
}

const SalesFunnels: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'templates' | 'my-funnels'>('templates');
  const [templates, setTemplates] = useState<Funnel[]>([]);
  const [userFunnels, setUserFunnels] = useState<Funnel[]>([]);
  const [selectedFunnel, setSelectedFunnel] = useState<Funnel | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [userTier, setUserTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<Funnel | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'course-launch',
    steps: [{ name: '', description: '', conversionRate: '' }],
    bestFor: [''],
    expectedROI: ''
  });

  useEffect(() => {
    fetchFunnels();
  }, []);

  const fetchFunnels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/sales-funnels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
      setUserFunnels(response.data.userFunnels || []);
      setCanCreate(response.data.canCreate || false);
      setUserTier(response.data.userTier || 'free');
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching sales funnels:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) {
      if (window.confirm('Sales funnel builder requires a Basic or higher plan. Upgrade now?')) {
        navigate('/pricing');
      }
      return;
    }
    setEditingFunnel(null);
    setFormData({
      name: '',
      description: '',
      category: 'course-launch',
      steps: [{ name: '', description: '', conversionRate: '' }],
      bestFor: [''],
      expectedROI: ''
    });
    setShowModal(true);
  };

  const openEditModal = (funnel: Funnel) => {
    setEditingFunnel(funnel);
    setFormData({
      name: funnel.name,
      description: funnel.description,
      category: funnel.category,
      steps: funnel.steps.length > 0
        ? funnel.steps.map(step => ({
            name: step.name,
            description: step.description,
            conversionRate: step.conversionRate || ''
          }))
        : [{ name: '', description: '', conversionRate: '' }],
      bestFor: funnel.best_for.length > 0 ? funnel.best_for : [''],
      expectedROI: funnel.expected_roi || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      // Filter out empty bestFor values
      const cleanedBestFor = formData.bestFor.filter(item => item.trim() !== '');
      const cleanedSteps = formData.steps.filter(step => step.name.trim() !== '');

      const endpoint = editingFunnel
        ? `${API_URL}/sales-funnels/${editingFunnel.id}`
        : `${API_URL}/sales-funnels`;
      const method = editingFunnel ? 'put' : 'post';

      await axios[method](endpoint, {
        ...formData,
        bestFor: cleanedBestFor,
        steps: cleanedSteps
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowModal(false);
      fetchFunnels();
      setActiveTab('my-funnels');
      alert(`Sales funnel ${editingFunnel ? 'updated' : 'created'} successfully!`);
    } catch (error: any) {
      console.error('Save funnel error:', error);
      alert(error.response?.data?.error || 'Failed to save sales funnel');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/sales-funnels/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFunnels();
      setSelectedFunnel(null);
      alert('Sales funnel deleted successfully');
    } catch (error: any) {
      console.error('Delete funnel error:', error);
      alert(error.response?.data?.error || 'Failed to delete sales funnel');
    }
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, { name: '', description: '', conversionRate: '' }]
    });
  };

  const removeStep = (index: number) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData({ ...formData, steps: newSteps.length > 0 ? newSteps : [{ name: '', description: '', conversionRate: '' }] });
  };

  const updateStep = (index: number, field: string, value: string) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  const addBestFor = () => {
    setFormData({ ...formData, bestFor: [...formData.bestFor, ''] });
  };

  const removeBestFor = (index: number) => {
    const newBestFor = formData.bestFor.filter((_, i) => i !== index);
    setFormData({ ...formData, bestFor: newBestFor.length > 0 ? newBestFor : [''] });
  };

  const updateBestFor = (index: number, value: string) => {
    const newBestFor = [...formData.bestFor];
    newBestFor[index] = value;
    setFormData({ ...formData, bestFor: newBestFor });
  };

  const currentFunnels = activeTab === 'templates' ? templates : userFunnels;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading sales funnels...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">🚀 Sales Funnels</h1>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'templates'
                  ? 'Proven, high-converting funnels for coaches and course creators'
                  : 'Create and manage your custom sales funnels'}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowHelpModal(true)}
                className="px-4 py-2 bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200 flex items-center gap-2 font-medium"
                title="How to create sales funnels"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Help
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
              {activeTab === 'my-funnels' && (
                <button
                  onClick={openCreateModal}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    canCreate
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  + Create Funnel
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex gap-8">
              <button
                onClick={() => setActiveTab('templates')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'templates'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📚 Templates ({templates.length})
              </button>
              <button
                onClick={() => setActiveTab('my-funnels')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'my-funnels'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⚡ My Funnels ({userFunnels.length})
                {!canCreate && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Basic+</span>}
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upgrade CTA for Free tier on My Funnels tab */}
        {activeTab === 'my-funnels' && !canCreate && (
          <div className="mb-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">🔒 Unlock Custom Sales Funnels</h3>
            <p className="mb-4">
              Create unlimited custom sales funnels with Basic tier or higher. Build funnels tailored to your business!
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Upgrade to Basic - $29/month
            </button>
          </div>
        )}

        {/* Funnels Grid */}
        {currentFunnels.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🚀</div>
            <p className="text-gray-500 text-lg">
              {activeTab === 'templates'
                ? 'No templates available yet'
                : canCreate
                  ? 'You haven\'t created any funnels yet. Click "Create Funnel" to get started!'
                  : 'Upgrade to Basic or higher to create custom sales funnels'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentFunnels.map((funnel) => (
              <div key={funnel.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
                  <h3 className="font-bold text-lg">{funnel.name}</h3>
                  <p className="text-sm text-indigo-100 mt-1">{funnel.category}</p>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{funnel.description}</p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Steps ({funnel.steps.length})</h4>
                    <div className="space-y-1">
                      {funnel.steps.slice(0, 3).map((step, idx) => (
                        <div key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-indigo-600 font-semibold">{idx + 1}.</span>
                          <span className="line-clamp-1">{step.name}</span>
                        </div>
                      ))}
                      {funnel.steps.length > 3 && (
                        <p className="text-xs text-gray-400 italic">+{funnel.steps.length - 3} more steps...</p>
                      )}
                    </div>
                  </div>

                  {funnel.expected_roi && (
                    <div className="mb-4 text-xs text-gray-600">
                      <span className="font-semibold">Expected ROI:</span> {funnel.expected_roi}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedFunnel(funnel)}
                      className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                    >
                      View Details
                    </button>
                    {activeTab === 'my-funnels' && (
                      <>
                        <button
                          onClick={() => openEditModal(funnel)}
                          className="py-2 px-4 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(funnel.id, funnel.name)}
                          className="py-2 px-4 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 text-sm"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Funnel Details Modal */}
      {selectedFunnel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">{selectedFunnel.name}</h2>
                  <p className="text-indigo-100 text-sm mt-1">{selectedFunnel.category}</p>
                </div>
                <button
                  onClick={() => setSelectedFunnel(null)}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600">{selectedFunnel.description}</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Funnel Steps</h3>
                <div className="space-y-4">
                  {selectedFunnel.steps.map((step, idx) => (
                    <div key={idx} className="border-l-4 border-indigo-500 pl-4 py-2 bg-gray-50 rounded">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{step.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                          {step.conversionRate && (
                            <p className="text-xs text-indigo-600 font-medium mt-1">
                              Expected Conversion: {step.conversionRate}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedFunnel.best_for && selectedFunnel.best_for.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Best For</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedFunnel.best_for.map((item, idx) => (
                      <span key={idx} className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedFunnel.expected_roi && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Expected ROI</h3>
                  <p className="text-gray-600">{selectedFunnel.expected_roi}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedFunnel(null)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingFunnel ? 'Edit Sales Funnel' : 'Create New Sales Funnel'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Funnel Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                    placeholder="e.g., Course Launch Funnel"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="course-launch">Course Launch</option>
                    <option value="webinar">Webinar Funnel</option>
                    <option value="lead-magnet">Lead Magnet</option>
                    <option value="email-sequence">Email Sequence</option>
                    <option value="membership">Membership</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows={3}
                  required
                  placeholder="Describe your sales funnel strategy..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected ROI</label>
                <input
                  type="text"
                  value={formData.expectedROI}
                  onChange={(e) => setFormData({ ...formData, expectedROI: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., 3-5x investment, $50k in first month"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Funnel Steps *</label>
                  <button
                    type="button"
                    onClick={addStep}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add Step
                  </button>
                </div>
                <div className="space-y-4">
                  {formData.steps.map((step, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-semibold text-gray-700">Step {idx + 1}</span>
                        {formData.steps.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeStep(idx)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={step.name}
                          onChange={(e) => updateStep(idx, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                          placeholder="Step name (e.g., Lead Magnet Landing Page)"
                          required
                        />
                        <textarea
                          value={step.description}
                          onChange={(e) => updateStep(idx, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                          rows={2}
                          placeholder="Step description..."
                        />
                        <input
                          type="text"
                          value={step.conversionRate || ''}
                          onChange={(e) => updateStep(idx, 'conversionRate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                          placeholder="Expected conversion rate (optional, e.g., 25-30%)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Best For (Target Audience)</label>
                  <button
                    type="button"
                    onClick={addBestFor}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add Item
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.bestFor.map((item, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateBestFor(idx, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                        placeholder="e.g., Coaches, Course Creators, Consultants"
                      />
                      {formData.bestFor.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBestFor(idx)}
                          className="px-3 py-2 text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingFunnel ? 'Update Funnel' : 'Create Funnel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={helpGuides.salesFunnels.title}
        steps={helpGuides.salesFunnels.steps}
      />
    </div>
  );
};

export default SalesFunnels;
